import React, { useMemo, useState } from 'react';
import { Card, Alert, Button } from 'react-bootstrap';
import { Form, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { filter, map, find } from 'lodash';
import { TrixEditor } from 'react-trix';
import { useUpdateEffect } from 'usehooks-ts';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FormGroupControl, FormGroupSelect } from '@components/Base';
import { useInsurersFetch } from '@components/Feature/Insurer';
import { useInsurerTeamsFetch } from '@components/Feature/InsurerTeam';
import { useBenefitTemplatesFetch } from '@components/Feature/BenefitTemplate';
import { useInsuranceTemplatesFetch } from '@components/Feature/InsuranceTemplate';
import { InsuranceTypesEnum, tInsuranceTypeEnum } from '@models/InsuranceType';
import { PolicyTypes, tPolicyType } from '@models/policyType';
import { noob } from '@utils/typescript';
import { useCompaniesFetch } from '@components/Feature/Company';

import { FormDisabled, FormRequired, FormValues } from './type';

const GROUP_PROPS = { className: 'mb-3' };

type Props = {
  disabled?: FormDisabled;
  required: FormRequired;
};

const InsurancePolicyBuyForm = ({ disabled, required }: Props) => {
  const { t } = useTranslation();
  const insurers = useInsurersFetch();
  const insurerTeams = useInsurerTeamsFetch();
  const benefitTemplates = useBenefitTemplatesFetch();
  const companies = useCompaniesFetch();
  const templates = useInsuranceTemplatesFetch();
  const { values, isSubmitting, setFieldValue } = useFormikContext<FormValues>();
  const { insuranceType, policyType, insurerId, insuranceTemplateId, descriptionHtml } = values;
  const [key, setKey] = useState(0);

  const filteredBenefitTemplates = useMemo(() => {
    return filter(benefitTemplates, (x) => x.insurerId == insurerId && x.insuranceType == insuranceType && x.policyType == policyType);
  }, [benefitTemplates, insurerId, policyType, insuranceType]);

  const filteredInsurerTeams = useMemo(() => {
    return filter(insurerTeams, (x) => x.insurerId == insurerId && x.insuranceType == insuranceType);
  }, [insurerTeams, insurerId, insuranceType]);

  const filteredTemplates = useMemo(() => {
    return filter(templates, (x) => x.insurerId == insurerId && x.insuranceType == insuranceType);
  }, [templates, insurerId, insuranceType]);

  useUpdateEffect(() => {
    if (!insuranceTemplateId) return;
    if (descriptionHtml) return;
    const template = find(templates, (x) => x.id == insuranceTemplateId);
    setFieldValue('descriptionHtml', template?.templateHtml);
    setKey((prev) => prev + 1);
  }, [insuranceTemplateId]);

  useUpdateEffect(() => {
    if (!insurerId) return;
    setFieldValue('insuranceTemplateId', '');
    setFieldValue('insurerTeamId', '');
  }, [insurerId]);

  useUpdateEffect(() => {
    if (policyType === PolicyTypes.Commercial) return;
    setFieldValue('companyId', '');
  }, [policyType]);

  return (
    <Form>
      <Alert variant="warning">{t('personalInfo.changeabilityWarningMessage')}</Alert>

      <Card>
        <Card.Body>
          <Card.Title>{t('common:fillPolicyDetails')}</Card.Title>

          <FormGroupSelect
            name="insurerId"
            label={t('common:insurer')}
            required={required.insurerId}
            disabled={disabled?.insurerId}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(insurers, ({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            name="insuranceType"
            label={t('common:insuranceType')}
            required={required.insuranceType}
            disabled={disabled?.insuranceType}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(InsuranceTypesEnum, (type) => (
              <option key={type} value={type}>
                {tInsuranceTypeEnum(type)}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            name="policyType"
            label={t('common:policyType')}
            required={required.policyType}
            disabled={disabled?.policyType}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(PolicyTypes, (type) => (
              <option key={type} value={type}>
                {tPolicyType(type)}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            name="companyId"
            label={t('common:company')}
            disabled={policyType !== PolicyTypes.Commercial}
            required={policyType === PolicyTypes.Commercial}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(companies, ({ id, name }) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            multiple
            name="benefitTemplateIds"
            label="Coverages"
            required={required.benefitTemplateIds}
            disabled={disabled?.benefitTemplateIds}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(filteredBenefitTemplates, ({ id, name }) => (
              <option key={name} value={id}>
                {name}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupSelect
            name="insurerTeamId"
            label="Insurer Team"
            required={required.insurerTeamId}
            disabled={disabled?.insurerTeamId}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(filteredInsurerTeams, ({ id, name }) => (
              <option key={name} value={id}>
                {name}
              </option>
            ))}
          </FormGroupSelect>

          <FormGroupControl
            name="effectiveDate"
            label={t('common:effectiveDate')}
            required={required.effectiveDate}
            disabled={disabled?.effectiveDate}
            groupProps={GROUP_PROPS}
            type="date"
          />

          <FormGroupControl
            name="expirationDate"
            label={t('common:expirationDate')}
            required={required.expirationDate}
            disabled={disabled?.expirationDate}
            groupProps={GROUP_PROPS}
            type="date"
          />

          <FormGroupControl
            name="adminFeeAmount"
            label={t('common:adminFee')}
            required={required.adminFeeAmount}
            disabled={disabled?.adminFeeAmount}
            groupProps={GROUP_PROPS}
            type="number"
          />

          <FormGroupSelect
            name="insuranceTemplateId"
            label="Policy Template"
            required={required.insuranceTemplateId}
            disabled={disabled?.insuranceTemplateId}
            groupProps={GROUP_PROPS}
          >
            <option></option>
            {map(filteredTemplates, ({ id, name }) => (
              <option key={id} value={id}> {name}</option>
            ))}
          </FormGroupSelect>

          <Card.Title className="mb-3">{t('common:fillDescriptionDetails')}</Card.Title>
          <TrixEditor
            key={key}
            className="trix-content"
            onChange={(html) => setFieldValue('descriptionHtml', html)}
            onEditorReady={noob}
            mergeTags={[]}
            value={values.descriptionHtml}
            placeholder={t('common:description')}
          />
        </Card.Body>

        <Card.Footer className="bg-white border-0">
          <div className="text-center my-4">
            <Button type="submit" variant="primary" size="lg" disabled={isSubmitting}>
              <span className="me-2">
                {t('common:summary')} & {t('common:review')}
              </span>
              <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </Form>
  );
};

export { InsurancePolicyBuyForm };
